<template>
    <div class="box-card">
      <div class="Subhead-heading mt-2">
        <h6>Theme preferences</h6>
      </div>
      <el-divider />
      <div>
        <p>
          Select a theme, or sync with your system and automatically switch
          between day and night themes.
        </p>
      </div>
      <h3 class="h5 mt-2" id="theme-mode-heading">Theme mode</h3>
      <div class="d-flex flex-items-center mb-3">
        <el-select
        @change="syncOptionChanged"
        :model-value="syncOption"
        @update:model-value="value => syncOption = value"
        class="mt-1 mr-2"
        style="width: 100"
      >
        <el-option label="Sync with system" value="sync" />
        <el-option label="Select a theme" value="select" />
      </el-select>
      <span v-if="syncOption === 'sync'"
        class="flex-auto color-fg-muted f6"
        >The theme will match your system active settings</span
      >
        <span v-else
            class="flex-auto color-fg-muted f6"
            >You can select a theme manually</span
            >
      </div>
      <div class="d-flex flex-column flex-md-row mb-3">
        <el-card
          class="col-md-5 mr-0 mr-md-3 mb-3 mb-md-0 appearance-preview" :class="{selectedHeader: themeMode === 'light'}"
        >
          <div>
            <div class="Box-header d-flex background-container" :class="{selectedHeader: themeMode === 'light'}">
              <h4 class="h5 flex-auto">
                <i class="ri-sun-line mr-2"></i>
                Day theme
              </h4>
              <span v-show="themeMode === 'light'"
                class="Label--info"
              >
                Active
              </span>
            </div>
            <div class="Box-body">
              <div class="color-fg-muted mb-3 f6" v-if="shouldSyncWithSystemTheme">
                This theme will be active when your system is set to “light mode”
              </div>
              <div
                data-theme="light"
                data-targets="appearance-preview.themePreviews"
                class="border mb-3 rounded-2"
              >
                <div class="border-bottom rounded-top-2 overflow-hidden">
                  <img
                    alt=""
                    class="d-block width-full"
                    src="https://github.githubassets.com/assets/light_preview-0fd4f11e117f.svg"
                  />
                </div>
                <div class="py-2 px-3 d-flex flex-items-center text"><input class="mr-2" v-if="!shouldSyncWithSystemTheme" v-model="themeMode" value="light" type="radio">Light default</div>
              </div>
            </div>
          </div>
        </el-card>
        <el-card class="col-md-5 appearance-preview" :class="{selectedHeader: themeMode === 'night'}">
          <div
            class="Box"
          >
            <div
              class="Box-header background-container d-flex" :class="{selectedHeader: themeMode === 'night'}"
            >
              <h4 class="h5 flex-auto">
                <i class="ri-moon-line mr-2"></i>
                Night theme
              </h4>
              <span v-show="themeMode === 'night'"
                class="Label--info"
              >
                Active
              </span>
            </div>
            <div data-view-component="true" class="Box-body">
              <div class="color-fg-muted mb-3 f6" v-if="shouldSyncWithSystemTheme">
                This theme will be active when your system is set to “dark mode”
              </div>
              <div
                data-theme="light"
                data-targets="appearance-preview.themePreviews"
                class="border mb-3 rounded-2"
                hidden=""
              >
                <div class="border-bottom rounded-top-2 overflow-hidden">
                  <img
                    alt=""
                    class="d-block width-full"
                    src="https://github.githubassets.com/assets/light_preview-0fd4f11e117f.svg"
                  />
                </div>
                <div class="py-2 px-3 text-bold">Light default</div>
              </div>
              <div
                data-theme="dark"
                data-targets="appearance-preview.themePreviews"
                class="border mb-3 rounded-2"
              >
                <div class="border-bottom rounded-top-2 overflow-hidden">
                  <img
                    alt=""
                    class="d-block width-full"
                    src="https://github.githubassets.com/assets/dark_preview-988b89718a06.svg"
                  />
                </div>
                <div class="py-2 px-3 d-flex flex-items-center text"><input class="mr-2" v-if="!shouldSyncWithSystemTheme" v-model="themeMode" value="night" type="radio">Dark default</div>
              </div>
            </div>
          </div>
        </el-card>
      </div>
    </div>
  </template>

<script>
import { onMounted, ref, watchEffect } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'AppearanceSettings',
  setup () {
    const syncOption = ref('sync')
    const themeMode = ref(null)
    const shouldSyncWithSystemTheme = ref(null)
    const store = useStore()

    const updateThemePreference = (theme) => {
      store.dispatch('theme/changeTheme', theme)
    }

    const syncOptionChanged = (value) => {
      if (value === 'sync') {
        shouldSyncWithSystemTheme.value = true
        localStorage.setItem('shouldSyncWithSystemTheme', true)
      } else {
        shouldSyncWithSystemTheme.value = false
        localStorage.setItem('shouldSyncWithSystemTheme', false)
      }
    }

    const loadUserPreferences = () => {
      shouldSyncWithSystemTheme.value = localStorage.getItem('shouldSyncWithSystemTheme') === null ? true : localStorage.getItem('shouldSyncWithSystemTheme') === 'true'
      if (shouldSyncWithSystemTheme.value) {
        updateThemeBasedOnSystem()
      } else {
        loadThemeFromLocalStorage()
      }
    }

    const loadThemeFromLocalStorage = () => {
      const savedTheme = localStorage.getItem('userThemePreference') ?? getSystemTheme()
      themeMode.value = savedTheme
      syncOption.value = 'select'
    }

    const getSystemTheme = () => {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'night' : 'light'
    }

    const updateThemeBasedOnSystem = () => {
      themeMode.value = getSystemTheme()
      syncOption.value = 'sync'
    }

    watchEffect(() => {
      if (themeMode.value === 'light') {
        localStorage.setItem('userThemePreference', 'light')
        updateThemePreference('light')
      } else if (themeMode.value === 'night') {
        localStorage.setItem('userThemePreference', 'night')
        updateThemePreference('night')
      }
    })

    watchEffect(() => {
      if (shouldSyncWithSystemTheme.value) {
        updateThemeBasedOnSystem()
      }
    })

    onMounted(() => {
      loadUserPreferences()

      watchEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
        // Listener function to update theme mode based on system changes
        const listener = (e) => {
          // Check if the user preference is set to sync before updating
          if (shouldSyncWithSystemTheme.value) {
            themeMode.value = e.matches ? 'night' : 'light'
          }
        }
        mediaQuery.addEventListener('change', listener)
        return () => {
          mediaQuery.removeEventListener('change', listener)
        }
      })
    })

    return {
      syncOption,
      syncOptionChanged,
      themeMode,
      shouldSyncWithSystemTheme
    }
  }
}
</script>

  <style scoped>

  .Box-body {
    padding: 1rem;
    padding-top: 10px;
  }
  .Subhead-heading {
    font-size: 1.5em;
  }

  .h5 {
    font-size: 14px;
    font-weight: 600;
  }

  .Label--info {
    border: 1px solid var(--accent-color);
    border-radius: 2em;
    display: inline-block;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 15px;
    padding: 0px 7px;
    white-space: nowrap;
    color: var(--accent-color);
  }

  .Box-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  margin: 0;
  border: 1px solid var(--border-color);
  background-color: #f6f8fa;
  }

  .Box-header.selectedHeader {
    background-color: #f1f8ff;
    border-bottom: 1px solid var(--accent-color);
  }

  .Box-header .h5 {
  display: flex;
  align-items: center;
  flex: 1;
  margin: 0;
  }

.Box-header svg {
  margin-right: 0.5rem;
}

  el-card {
    padding: 0 !important;
  }

  .color-fg-muted {
    color: var(--muted-text-color);
    font-size: small;
    word-wrap: break-word;
    margin-bottom: 16px;
    margin-top: 10px;
    line-height: 1.4;
  }

  p {
    font-size: 14px;
    color: black;
  }

  .appearance-preview .el-card__body {
  padding: 0 !important;
}

.appearance-preview {
  padding: 0 !important; /* Only remove padding for this specific card */
}
  </style>

  <style>
  .appearance-preview .el-card__body {
  padding: 0 !important;

}

.appearance-preview {
  padding: 0 !important;
  height: fit-content !important;
}

.flex-auto {
    flex: auto;
}

.width-full {
    width: 100%;
}

.el-card {
  border: 1px solid var(--border-color) !important;
}

.el-card.selectedHeader {
  border: 1px solid var(--accent-color) !important;

}

.el-menu-item.is-active i {
  color: var(--accent-color) !important;
}

.el-menu-item.is-active span {
  color: var(--accent-color) !important;
}
</style>
